import { io, type Socket } from "socket.io-client";
import { getAccessToken } from "@/api/client";

function normalizeBaseUrl(url: string | undefined | null): string {
  return String(url ?? "").replace(/\/$/, "");
}

const BASE_URL = normalizeBaseUrl(
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3001",
);

let shared: Socket | null = null;

export type ProjectRealtimePayload = {
  projectId: string;
  productType: "toy" | "story" | "animation" | "game";
  status: string;
  title?: string | null;
  jobId?: string | null;
  jobStatus?:
    | "pending"
    | "running"
    | "succeeded"
    | "failed"
    | "cancelled"
    | null;
  progress?: { current: number; total: number };
  characterMediaId?: string | null;
  characterImageUrl?: string | null;
  modelUrl?: string | null;
  referenceImageUrl?: string | null;
  frontViewUrl?: string | null;
  backViewUrl?: string | null;
  glbObjectKey?: string | null;
  analysis?: unknown;
  coverUrl?: string | null;
  coverBackUrl?: string | null;
  language?: string | null;
};

/**
 * When VITE_API_BASE_URL is a relative prefix (e.g. `/api`), Socket.IO must
 * use the Vite/nginx proxy path `/api/socket.io` and namespace `/projects`.
 * Absolute API URLs talk to `/socket.io` on the API host directly.
 */
function socketConnectionOptions(): {
  url: string;
  path: string;
} {
  if (BASE_URL.startsWith("/")) {
    return {
      url: "/projects",
      path: `${BASE_URL}/socket.io`,
    };
  }
  return {
    url: `${BASE_URL}/projects`,
    path: "/socket.io",
  };
}

export function getProjectSocket(): Socket {
  if (shared && shared.connected) {
    return shared;
  }
  if (shared) {
    shared.auth = { token: getAccessToken() ?? "" };
    if (!shared.connected) {
      shared.connect();
    }
    return shared;
  }

  const { url, path } = socketConnectionOptions();

  shared = io(url, {
    path,
    autoConnect: true,
    transports: ["websocket", "polling"],
    auth: {
      token: getAccessToken() ?? "",
    },
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });

  shared.on("connect_error", (err) => {
    console.warn("[projectSocket] connect_error", err.message);
  });

  return shared;
}

export function refreshProjectSocketAuth(): void {
  if (!shared) return;
  shared.auth = { token: getAccessToken() ?? "" };
}

export function disconnectProjectSocket(): void {
  if (!shared) return;
  shared.disconnect();
  shared = null;
}
